import { useId } from "react";

/**
 * MakerDoc brand lockup — the layered-sheets mark + a "MakerDoc" wordmark in
 * the brand typeface (Manrope, loaded app-wide). The mark is a stack of three
 * sheets in the MakerX palette (electric → magenta → magenta·gold gradient)
 * with live-collaborator dots and an AI spark: the multiplayer-document story.
 * The sheet fills are near-black, so it sits cleanly on dark surfaces.
 */

/** The layered-sheets glyph alone (transparent background). Self-contained SVG
 *  with per-instance gradient/filter ids so multiple marks on a page don't
 *  collide. */
export function MakerDocMark({ className }: { className?: string }) {
  // Per-instance ids (colons stripped — invalid in url(#…) references).
  const id = useId().replace(/:/g, "");
  const grad = `mdmark-grad-${id}`;
  const fill = `mdmark-fill-${id}`;
  const shadow = `mdmark-shadow-${id}`;
  return (
    <svg
      viewBox="0 0 256 256"
      className={className}
      role="img"
      aria-label="makerdoc.ai"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* signature magenta -> gold wing, across the front sheet */}
        <linearGradient
          id={grad}
          x1="92"
          y1="80"
          x2="212"
          y2="232"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#cc3a9d" />
          <stop offset="1" stopColor="#ffc023" />
        </linearGradient>
        {/* subtle interior lift on each sheet */}
        <linearGradient id={fill} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#14161c" />
          <stop offset="1" stopColor="#0c0d10" />
        </linearGradient>
        <filter id={shadow} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow
            dx="0"
            dy="6"
            stdDeviation="9"
            floodColor="#000000"
            floodOpacity="0.45"
          />
        </filter>
      </defs>

      {/* back sheet: an earlier version / another contributor */}
      <rect
        x="36"
        y="24"
        width="120"
        height="152"
        rx="18"
        fill={`url(#${fill})`}
        stroke="#16acf2"
        strokeWidth="9"
      />
      {/* middle sheet */}
      <rect
        x="64"
        y="52"
        width="120"
        height="152"
        rx="18"
        fill={`url(#${fill})`}
        stroke="#cc3a9d"
        strokeWidth="9"
      />
      {/* front sheet: the live document */}
      <g filter={`url(#${shadow})`}>
        <rect
          x="92"
          y="80"
          width="120"
          height="152"
          rx="18"
          fill={`url(#${fill})`}
          stroke={`url(#${grad})`}
          strokeWidth="9"
        />
        <line
          x1="110"
          y1="124"
          x2="188"
          y2="124"
          stroke={`url(#${grad})`}
          strokeWidth="11"
          strokeLinecap="round"
        />
        <g stroke="#16acf2" strokeLinecap="round" opacity="0.9">
          <line x1="110" y1="148" x2="194" y2="148" strokeWidth="8" />
          <line x1="110" y1="168" x2="194" y2="168" strokeWidth="8" />
          <line x1="110" y1="188" x2="166" y2="188" strokeWidth="8" />
        </g>
        {/* AI spark = generated / improving */}
        <path
          d="M186 196 C188 206 192 208 202 210 C192 212 188 214 186 224 C184 214 180 212 170 210 C180 208 184 206 186 196 Z"
          fill="#ffc023"
        />
      </g>

      {/* live collaborators present on the doc (multiplayer) */}
      <g>
        <circle cx="150" cy="80" r="17" fill="#0e0f10" />
        <circle cx="150" cy="80" r="12" fill="#16acf2" />
        <circle cx="176" cy="80" r="17" fill="#0e0f10" />
        <circle cx="176" cy="80" r="12" fill="#ffc023" />
      </g>
    </svg>
  );
}

/** Full lockup: mark + "MakerDoc" wordmark. `markClassName` sizes the glyph. */
export function MakerDocLogo({
  className,
  markClassName = "h-7 w-auto",
}: {
  className?: string;
  markClassName?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className ?? ""}`}
      data-testid="makerdoc-logo"
    >
      <MakerDocMark className={markClassName} />
      <span className="text-[1.15rem] font-light tracking-[-0.02em] leading-none lowercase">
        makerdoc
        <span className="font-medium text-[var(--color-mx-electric)]">.ai</span>
      </span>
    </span>
  );
}
